import type { User } from "schemas/generated/user";

import * as React from "react";

import { Badge } from "@replohq/lattice/components/badge/Badge";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@replohq/lattice/components/menu/Menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@replohq/lattice/components/sidebar/Sidebar";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import { Avatar } from "@v2/components/EditorAvatar";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { authRouter } from "@v2/trpc/routes/auth";
import { routes } from "@v2/utils/router";
import { storeToken } from "@v2/utils/store-token";
import { ChevronsUpDown, Coins, LogOut, UserIcon } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";
import { clearLastVisitedWorkspaceId } from "replo-utils/lib/lastVisitedDashboardIds";

const NavUserHeader = ({ user }: { user: User }) => {
  // (Ezra, 2026-07-13, REPL-29254): Compose the trigger onto the existing control so the DOM contains one button instead of nested buttons.
  return (
    <SidebarMenuButton
      render={<MenuTrigger className="w-full" />}
      size="lg"
      className="data-[state=open]:bg-menu-item-selected data-[state=open]:text-default items-start"
    >
      <div className="flex gap-2 justify-center items-center h-full">
        <Badge size="xl">
          <Avatar name={user.name} isSquare />
        </Badge>

        <div className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate header-h6 line-clamp-1">{user.name}</span>

          <span className="truncate text-xs text-muted line-clamp-1">
            {user.email}
          </span>
        </div>
      </div>
      <ChevronsUpDown className="ml-auto size-3! mt-1" />
    </SidebarMenuButton>
  );
};

export function NavUser() {
  const { isMobile } = useSidebar();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const logEvent = useLogAnalytics();

  const { mutateAsync: logout } = authRouter.logout.useMutation({
    onSuccess: async () => {
      storeToken(null);
      window.location.replace("/auth/login");
    },
  });

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Menu>
          {user ? (
            <NavUserHeader user={user} />
          ) : (
            <MenuTrigger className="w-full">
              <Skeleton className="w-full h-12" />
            </MenuTrigger>
          )}

          <MenuContent
            align="end"
            alignOffset={isMobile ? 0 : -12}
            side={isMobile ? "bottom" : "right"}
            sideOffset={isMobile ? 0 : 16}
          >
            <MenuItem
              label="My Account"
              startEnhancer={<UserIcon />}
              onClick={() => {
                logEvent("dashboard.nav.click", {
                  tab: "your_profile",
                  userId: user?.id ?? null,
                  workspaceId: workspaceId ?? null,
                });
                navigate(
                  generatePath(routes.shops.settings.account.profile, {
                    projectId,
                  }),
                );
              }}
            />
            <MenuItem
              label="My Referrals"
              startEnhancer={<Coins />}
              onClick={() => {
                logEvent("dashboard.nav.click", {
                  tab: "my_referrals",
                  userId: user?.id ?? null,
                  workspaceId: workspaceId ?? null,
                });
                navigate(
                  generatePath(routes.shops.settings.account.referrals, {
                    projectId,
                  }),
                );
              }}
            />
            <MenuSeparator />
            <MenuItem
              label="Log out"
              startEnhancer={<LogOut />}
              onClick={() => {
                logEvent("dashboard.logout", {});
                void logout({});

                // Note (Noah, 2025-02-22): Make sure to clear the last visited
                // workspace when logging out, otherwise when you log in again to
                // a new account you'll see a permissions error by default
                clearLastVisitedWorkspaceId();
              }}
            />
          </MenuContent>
        </Menu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
